/**
 * AutomationTabContent
 * Automation tab with "Fix It With Me" launcher and script preview
 * Renders automation script content as markdown
 */

import { makeStyles, tokens, Text, Button, Tooltip } from '@fluentui/react-components'
import { PlayRegular, CodeRegular, InfoRegular } from '@fluentui/react-icons'
import type { IssueDetails } from '../../../../domain'
import { MarkdownContentRenderer } from '../panels'
import { FeedbackLink, InlineEmptyState, TabContentLayout } from '../shared'

const useStyles = makeStyles({
  launchSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  launchHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  launchIcon: {
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
  },
  launchTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  launchDescription: {
    color: tokens.colorNeutralForeground2,
  },
  launchButton: {
    alignSelf: 'flex-start',
    marginTop: tokens.spacingVerticalS,
  },
  disabledReason: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  infoIcon: {
    fontSize: '14px',
  },
  scriptSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  scriptHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  scriptIcon: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2,
  },
  scriptTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
})

export interface AutomationTabContentProps {
  issue: IssueDetails
  onLaunchAutomation?: () => void
}

export function AutomationTabContent({ issue, onLaunchAutomation }: AutomationTabContentProps) {
  const styles = useStyles()

  const automation = issue.automationScript

  if (!automation) {
    return (
      <TabContentLayout>
        <InlineEmptyState message="No automation available for this issue type." withBackground />
        <FeedbackLink context="automation-empty" />
      </TabContentLayout>
    )
  }

  const handleLaunch = () => {
    if (onLaunchAutomation) {
      onLaunchAutomation()
    } else {
      console.log('Launching automation:', automation.id)
    }
  }

  return (
    <TabContentLayout>
      <div className={styles.launchSection}>
        <div className={styles.launchHeader}>
          <PlayRegular className={styles.launchIcon} />
          <Text className={styles.launchTitle}>Fix It With Me</Text>
        </div>
        <Text size={200} className={styles.launchDescription}>
          Launch an automated guided fix that walks you through the resolution process step by step.
        </Text>
        {automation.canLaunch ? (
          <Button
            appearance="primary"
            icon={<PlayRegular />}
            className={styles.launchButton}
            onClick={handleLaunch}
          >
            Launch {automation.title}
          </Button>
        ) : (
          <>
            <Tooltip content={automation.launchDisabledReason || 'Automation unavailable'} relationship="label">
              <Button
                appearance="primary"
                icon={<PlayRegular />}
                className={styles.launchButton}
                disabled
              >
                Launch {automation.title}
              </Button>
            </Tooltip>
            {automation.launchDisabledReason && (
              <div className={styles.disabledReason}>
                <InfoRegular className={styles.infoIcon} />
                <Text size={200}>{automation.launchDisabledReason}</Text>
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles.scriptSection}>
        <div className={styles.scriptHeader}>
          <CodeRegular className={styles.scriptIcon} />
          <Text className={styles.scriptTitle}>Script Preview</Text>
        </div>
        <MarkdownContentRenderer content={automation.content} />
      </div>

      <FeedbackLink context="automation" />
    </TabContentLayout>
  )
}
